import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { ShieldCheck } from "lucide-react";
import Logo from "./Logo";

const STORAGE_KEY = "dg_age_verified";

export default function AgeGate() {
    const [verified, setVerified] = useState(() => localStorage.getItem(STORAGE_KEY) === "1");
    const [declined, setDeclined] = useState(false);
    const [closing, setClosing] = useState(false);

    useEffect(() => {
        document.body.style.overflow = verified ? "" : "hidden";
        return () => {
            document.body.style.overflow = "";
        };
    }, [verified]);

    if (verified) return null;

    const confirm = () => {
        setClosing(true);
        setTimeout(() => {
            localStorage.setItem(STORAGE_KEY, "1");
            setVerified(true);
        }, 550);
    };

    return (
        <motion.div
            initial={{ opacity: 1 }}
            animate={{ opacity: closing ? 0 : 1, scale: closing ? 1.04 : 1 }}
            transition={{ duration: 0.55, ease: [0.22, 1, 0.36, 1] }}
            className="fixed inset-0 z-[100] flex items-center justify-center bg-ink"
            data-testid="age-gate"
        >
            <div
                className="absolute inset-0"
                style={{
                    background:
                        "radial-gradient(55% 45% at 50% 38%, rgba(76,175,80,0.16), transparent 70%), radial-gradient(30% 30% at 80% 80%, rgba(212,175,55,0.08), transparent 70%)",
                }}
            />
            <motion.div
                initial={{ y: 32, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ delay: 0.15, duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
                className="relative z-10 w-full max-w-md mx-4 rounded-3xl border border-leaf/20 bg-moss/60 backdrop-blur-xl p-8 md:p-10 text-center"
            >
                <div className="flex justify-center">
                    <Logo className="h-16" alt="District Greenz DMV" />
                </div>
                <h1 className="mt-8 font-display text-3xl md:text-4xl font-extrabold text-bone leading-tight">
                    Are you 21 or older?
                </h1>
                <p className="mt-4 text-sm text-sage leading-relaxed">
                    You must be at least 21 years old with a valid government-issued ID to enter this site and receive
                    cannabis products in Washington, DC.
                </p>

                {!declined ? (
                    <div className="mt-8 flex flex-col gap-3">
                        <button
                            data-testid="age-gate-confirm"
                            onClick={confirm}
                            className="w-full rounded-full bg-leaf py-4 font-bold text-ink hover:bg-leaf-bright transition-colors"
                        >
                            Yes — I'm 21 or older
                        </button>
                        <button
                            data-testid="age-gate-deny"
                            onClick={() => setDeclined(true)}
                            className="w-full rounded-full border border-bone/20 py-4 font-semibold text-bone/80 hover:border-leaf/40 transition-colors"
                        >
                            No, I'm under 21
                        </button>
                    </div>
                ) : (
                    <div className="mt-8 rounded-2xl border border-gold/30 bg-gold/5 p-5" data-testid="age-gate-declined">
                        <ShieldCheck size={22} className="mx-auto text-gold" />
                        <p className="mt-3 text-sm text-bone/85 leading-relaxed">
                            Sorry — you must be 21 or older to visit District Greenz DMV. Come back when you're of age.
                        </p>
                    </div>
                )}
            </motion.div>
        </motion.div>
    );
}
